(function () {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const finePointer = window.matchMedia("(pointer: fine)");
  const coarsePointer = window.matchMedia("(pointer: coarse)");
  const compactViewport = window.matchMedia("(max-width: 989px)");
  let contexts = [];
  let cleanups = [];

  window.BouquetAnimations = window.BouquetAnimations || {};
  window.BouquetAnimations.handlesHomeMotion = true;

  function canAnimate() {
    return !prefersReducedMotion.matches && !coarsePointer.matches && !compactViewport.matches && typeof window.gsap !== "undefined";
  }

  function hasScrollTrigger() {
    return typeof window.ScrollTrigger !== "undefined";
  }

  function toArray(selector, scope = document) {
    return Array.from(scope.querySelectorAll(selector)).filter(Boolean);
  }

  function first(selector, scope = document) {
    return scope.querySelector(selector);
  }

  function addCleanup(cleanup) {
    cleanups.push(cleanup);
  }

  function resetAnimations() {
    contexts.forEach((context) => context.revert());
    contexts = [];

    cleanups.forEach((cleanup) => cleanup());
    cleanups = [];
  }

  function withContext(callback) {
    contexts.push(window.gsap.context(callback));
  }

  function revealBatch(items, trigger, vars = {}) {
    if (!items.length) return;

    const gsap = window.gsap;
    const config = {
      y: vars.y ?? 30,
      x: vars.x ?? 0,
      opacity: 0,
      duration: vars.duration ?? 0.72,
      stagger: vars.stagger ?? 0.08,
      ease: vars.ease || "power3.out",
      clearProps: vars.clearProps || "transform,opacity",
    };

    if (hasScrollTrigger()) {
      config.scrollTrigger = {
        trigger: trigger || items[0],
        start: vars.start || "top 88%",
        once: true,
      };
    }

    gsap.from(items, config);
  }

  function initHeroAnimations() {
    const hero = first('[data-gsap="hero"]');
    if (!hero) return;

    const gsap = window.gsap;
    const navbar = first('[data-gsap="navbar"]');
    const label = first('[data-gsap="hero-label"]', hero);
    const title = first('[data-gsap="hero-title"]', hero);
    const titleLines = title ? toArray(":scope > span", title) : [];
    const emphasis = first('[data-gsap="hero-title"] em', hero);
    const text = first('[data-gsap="hero-text"]', hero);
    const buttons = toArray('[data-gsap="hero-button"]', hero);
    const benefits = toArray('[data-gsap="benefit"]', hero);
    const imageWrap = first('[data-gsap="hero-image"]', hero);
    const heroImage = imageWrap?.querySelector("img") || imageWrap;

    withContext(() => {
      const timeline = gsap.timeline({ defaults: { ease: "power3.out" } });

      if (navbar) {
        timeline.from(navbar, { y: -18, opacity: 0, duration: 0.72, clearProps: "transform,opacity" }, 0.04);
      }

      if (label) {
        timeline.from(label, { y: 14, opacity: 0, duration: 0.68, clearProps: "transform,opacity" }, 0.12);
      }

      if (titleLines.length) {
        timeline.from(titleLines, {
          y: 32,
          opacity: 0,
          duration: 0.92,
          stagger: 0.12,
          ease: "expo.out",
          clearProps: "transform,opacity",
        }, 0.24);
      }

      if (emphasis) {
        timeline.from(emphasis, { y: 18, opacity: 0, duration: 0.82, clearProps: "transform,opacity" }, 0.5);
      }

      if (text) {
        timeline.from(text, { y: 18, opacity: 0, duration: 0.72, clearProps: "transform,opacity" }, 0.64);
      }

      if (buttons.length) {
        timeline.from(buttons, {
          y: 18,
          opacity: 0,
          duration: 0.68,
          stagger: 0.1,
          clearProps: "transform,opacity",
        }, 0.76);
      }

      if (benefits.length) {
        timeline.from(benefits, {
          y: 16,
          opacity: 0,
          duration: 0.62,
          stagger: 0.1,
          clearProps: "transform,opacity",
        }, 0.9);
      }

      if (heroImage) {
        timeline.from(heroImage, {
          y: 44,
          scale: 1.035,
          opacity: 0,
          duration: 1.08,
          ease: "expo.out",
          clearProps: "opacity",
        }, 0.18);

        if (hasScrollTrigger() && window.matchMedia("(min-width: 750px)").matches) {
          gsap.to(heroImage, {
            yPercent: 4,
            scale: 1.045,
            ease: "none",
            scrollTrigger: {
              trigger: hero,
              start: "top top",
              end: "bottom top",
              scrub: 0.8,
            },
          });
        }
      }
    });
  }

  function initCollectionAnimations() {
    const homeCards = toArray('[data-gsap="collection-card"]');
    const collectionRoot = first("[data-collection-bouquet]");
    const collectionHero = first('[data-gsap="collection-hero"]');
    const filters = first('[data-gsap="filters"]');
    const productGrid = first("[data-products-layout]");
    const productCards = toArray('[data-gsap="product-card"]');
    const pagination = first('[data-gsap="pagination"]');

    withContext(() => {
      revealBatch(homeCards, first(".bouquet-collections"), { y: 34, stagger: 0.07 });

      if (collectionHero) {
        revealBatch([collectionHero], collectionHero, { y: 26, duration: 0.84, start: "top 92%" });
      }

      if (filters) {
        revealBatch([filters], collectionRoot || filters, { x: -24, y: 0, duration: 0.72, start: "top 82%" });
      }

      if (productCards.length) {
        revealBatch(productCards, productGrid || productCards[0], { y: 30, stagger: 0.045, start: "top 86%" });
      }

      if (pagination) {
        revealBatch([pagination], pagination, { y: 16, duration: 0.54, start: "top 94%" });
      }
    });

    if (collectionRoot && productGrid) {
      const fadeGrid = () => window.gsap.to(productGrid, { opacity: 0, y: 10, duration: 0.22, ease: "power2.out" });
      collectionRoot.querySelector("[data-bouquet-filters-form]")?.addEventListener("submit", fadeGrid);
      collectionRoot.querySelectorAll("[data-collection-sort], [data-per-page-select]").forEach((control) => {
        control.addEventListener("change", fadeGrid);
      });
      addCleanup(() => {
        collectionRoot.querySelector("[data-bouquet-filters-form]")?.removeEventListener("submit", fadeGrid);
        collectionRoot.querySelectorAll("[data-collection-sort], [data-per-page-select]").forEach((control) => {
          control.removeEventListener("change", fadeGrid);
        });
      });
    }
  }

  function initBannerAnimations() {
    const banner = first('[data-gsap="delivery-banner"]');
    if (!banner) return;

    const image = first('[data-gsap="delivery-image"] img, .bouquet-delivery__media img', banner);
    const copy = first('[data-gsap="delivery-copy"], .bouquet-delivery__content', banner);
    const badge = first('[data-gsap="delivery-badge"], .bouquet-delivery__stamp', banner);
    const button = first('[data-gsap="delivery-button"], .bouquet-delivery__content .bouquet-button', banner);

    withContext(() => {
      if (image) {
        window.gsap.from(image, {
          scale: 1.045,
          duration: 1.15,
          ease: "power3.out",
          scrollTrigger: hasScrollTrigger() ? { trigger: banner, start: "top 82%", once: true } : undefined,
          clearProps: "transform",
        });
      }

      revealBatch([copy].filter(Boolean), banner, { x: -28, y: 0, duration: 0.78 });

      if (badge) {
        window.gsap.from(badge, {
          rotate: -4,
          scale: 0.92,
          opacity: 0,
          duration: 0.72,
          ease: "power3.out",
          scrollTrigger: hasScrollTrigger() ? { trigger: banner, start: "top 82%", once: true } : undefined,
          clearProps: "transform,opacity",
        });
      }

      revealBatch([button].filter(Boolean), banner, { y: 14, duration: 0.58, start: "top 82%" });
    });
  }

  function initBenefitAnimations() {
    const benefits = toArray('.bouquet-benefits [data-gsap="benefit"]');
    if (!benefits.length) return;

    withContext(() => {
      window.gsap.from(benefits, {
        y: 24,
        scale: 0.96,
        opacity: 0,
        duration: 0.68,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: hasScrollTrigger() ? {
          trigger: first(".bouquet-benefits") || benefits[0],
          start: "top 86%",
          once: true,
        } : undefined,
        clearProps: "transform,opacity",
      });
    });
  }

  function initFeaturedProductAnimations() {
    const featured = first('[data-gsap="featured-product"]');
    if (!featured) return;

    const activeSlide = first(".bouquet-featured__slide.is-active", featured) || first(".bouquet-featured__slide", featured);
    const media = activeSlide ? first(".bouquet-featured__media", activeSlide) : null;
    const content = activeSlide ? first(".bouquet-featured__content", activeSlide) : null;
    const button = activeSlide ? first(".bouquet-button", activeSlide) : null;
    const arrows = toArray(".bouquet-featured__arrow", featured);

    withContext(() => {
      revealBatch([media].filter(Boolean), featured, { x: -28, y: 0, duration: 0.78, start: "top 84%" });
      revealBatch([content].filter(Boolean), featured, { x: 28, y: 0, duration: 0.78, start: "top 84%" });
      revealBatch([button, ...arrows].filter(Boolean), featured, { y: 12, duration: 0.58, stagger: 0.08, start: "top 84%" });
    });
  }

  function initInstagramAnimations() {
    const section = first('[data-gsap="instagram"]');
    if (!section) return;

    const items = toArray('[data-gsap="instagram-item"]', section);
    withContext(() => {
      revealBatch(items, section, { y: 22, stagger: 0.08, start: "top 88%" });
    });
  }

  function initProductPageAnimations() {
    const page = first('[data-gsap="product-page"]');
    if (!page) return;

    const gallery = first('[data-gsap="product-gallery"]', page);
    const image = first('[data-gsap="product-main-image"]', page);
    const info = first('[data-gsap="product-info"]', page);
    const options = toArray('[data-gsap="variant-option"]', page);
    const submit = first('[data-gsap="cart-button"]', page);
    const accordions = toArray('[data-gsap="accordion"]', page);

    withContext(() => {
      revealBatch([gallery].filter(Boolean), page, { y: 24, duration: 0.82, start: "top 90%" });

      if (image) {
        window.gsap.from(image, { scale: 1.025, opacity: 0, duration: 0.92, ease: "power3.out", clearProps: "transform,opacity" });
      }

      revealBatch([info].filter(Boolean), page, { x: 28, y: 0, duration: 0.84, start: "top 90%" });
      revealBatch(options, info || page, { y: 18, stagger: 0.06, duration: 0.58, start: "top 92%" });
      revealBatch([submit].filter(Boolean), info || page, { y: 12, duration: 0.52, start: "top 92%" });
      revealBatch(accordions, gallery || page, { y: 14, stagger: 0.06, duration: 0.52, start: "top 92%" });
    });
  }

  function initCartAnimations() {
    const cart = first('[data-gsap="cart-page"]');
    if (!cart) return;

    const items = toArray('[data-gsap="cart-item"]', cart);
    const summary = first('[data-gsap="cart-summary"]', cart);

    withContext(() => {
      revealBatch(items, cart, { y: 22, stagger: 0.07, start: "top 90%" });
      revealBatch([summary].filter(Boolean), cart, { x: 26, y: 0, duration: 0.74, start: "top 90%" });
    });
  }

  function initNavbarAnimations() {
    const navbar = first('[data-gsap="navbar"]');
    const mobileDrawer = first('[data-gsap="mobile-menu"]');
    if (!navbar && !mobileDrawer) return;

    if (mobileDrawer) {
      const observer = new MutationObserver(() => {
        if (!mobileDrawer.classList.contains("is-open")) return;
        const links = toArray(".pill-mobile-menu__link, .pill-mobile-menu__item, .pill-drawer__quick a", mobileDrawer);
        window.gsap.fromTo(
          links,
          { x: 16, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.42, stagger: 0.045, ease: "power3.out", clearProps: "transform,opacity" }
        );
      });
      observer.observe(mobileDrawer, { attributes: true, attributeFilter: ["class"] });
      addCleanup(() => observer.disconnect());
    }
  }

  function initFooterAnimations() {
    const footer = first('[data-gsap="footer"]');
    if (!footer) return;

    const columns = toArray('[data-gsap="footer-column"]', footer);
    withContext(() => {
      revealBatch([footer], footer, { y: 26, duration: 0.7, start: "top 94%" });
      revealBatch(columns, footer, { y: 20, stagger: 0.08, duration: 0.62, start: "top 90%" });
    });
  }

  function initMiniCartAnimations() {
    const drawer = first('[data-gsap="mini-cart"]');
    if (!drawer) return;

    const panel = first('[data-gsap="mini-cart-panel"]', drawer);
    const overlay = first('[data-gsap="mini-cart-overlay"]', drawer);

    const animateOpen = () => {
      if (!drawer.classList.contains("active")) return;

      window.gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.22, ease: "power2.out" });
      window.gsap.fromTo(panel, { x: 28, opacity: 0.96 }, { x: 0, opacity: 1, duration: 0.34, ease: "power3.out", clearProps: "transform,opacity" });
      window.gsap.fromTo(
        toArray('[data-gsap="mini-cart-item"]', drawer),
        { x: 18, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.38, stagger: 0.045, ease: "power3.out", clearProps: "transform,opacity" }
      );
    };

    const observer = new MutationObserver(animateOpen);
    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });
    addCleanup(() => observer.disconnect());
  }

  function initHoverMicroInteractions() {
    if (!finePointer.matches) return;

    toArray('[data-gsap="collection-card"], [data-gsap="product-card"]').forEach((card) => {
      const image = card.querySelector("img");
      const arrow = card.querySelector(".bouquet-round-arrow, .product-card-bouquet__quick");
      const enter = () => {
        window.gsap.to(card, { y: -4, duration: 0.24, ease: "power2.out" });
        if (image) window.gsap.to(image, { scale: 1.035, duration: 0.32, ease: "power2.out" });
        if (arrow) window.gsap.to(arrow, { x: 4, scale: 1.04, opacity: 1, duration: 0.22, ease: "power2.out" });
      };
      const leave = () => {
        window.gsap.to(card, { y: 0, duration: 0.28, ease: "power2.out", clearProps: "transform" });
        if (image) window.gsap.to(image, { scale: 1, duration: 0.34, ease: "power2.out", clearProps: "transform" });
        if (arrow) window.gsap.to(arrow, { x: 0, scale: 1, duration: 0.24, ease: "power2.out", clearProps: "transform" });
      };

      card.addEventListener("pointerenter", enter);
      card.addEventListener("pointerleave", leave);
      addCleanup(() => {
        card.removeEventListener("pointerenter", enter);
        card.removeEventListener("pointerleave", leave);
      });
    });

    toArray(".flower-submit, .cart-quantity button, .flower-quantity button").forEach((control) => {
      const pulse = () => window.gsap.fromTo(control, { scale: 0.98 }, { scale: 1, duration: 0.22, ease: "power2.out", clearProps: "transform" });
      control.addEventListener("pointerdown", pulse);
      addCleanup(() => control.removeEventListener("pointerdown", pulse));
    });
  }

  function initAllAnimations() {
    resetAnimations();
    if (!canAnimate()) return;

    if (hasScrollTrigger()) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }

    initHeroAnimations();
    initCollectionAnimations();
    initBannerAnimations();
    initBenefitAnimations();
    initFeaturedProductAnimations();
    initInstagramAnimations();
    initProductPageAnimations();
    initCartAnimations();
    initNavbarAnimations();
    initFooterAnimations();
    initMiniCartAnimations();
    initHoverMicroInteractions();

    window.ScrollTrigger?.refresh();
  }

  window.BouquetAnimations.refresh = initAllAnimations;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllAnimations, { once: true });
  } else {
    initAllAnimations();
  }

  document.addEventListener("shopify:section:load", () => {
    window.requestAnimationFrame(initAllAnimations);
  });

  document.addEventListener("shopify:section:unload", resetAnimations);
  window.addEventListener("pagehide", resetAnimations);
})();
